// ============================================================
// HandoutForm (v8.363.10) - a note or a link, new or edited, in the drawer.
// ============================================================
import { useState, type FormEvent } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/HandoutsDrawer.module.css';
import type { HandoutKind } from '../engine/handouts';

export interface HandoutFormValues {
  title: string;
  folder: string;
  tags: string[];
  notes: string;
  url: string;
}

interface Props {
  kind: HandoutKind;
  initial?: Partial<HandoutFormValues>;
  onSave: (values: HandoutFormValues) => Promise<unknown> | unknown;
  onCancel: () => void;
}

/** A note needs words; a link needs an http(s) address; other kinds need nothing. */
function isValidHandout(kind: HandoutKind, notes: string, url: string): boolean {
  if (kind === 'note') return notes.trim().length > 0;
  if (kind === 'link') return /^https?:\/\//i.test(url.trim());
  return true;
}

/** The form's five fields as typed, starting from `initial` (tags as one comma-separated line). */
function useHandoutDraft(initial: Partial<HandoutFormValues>) {
  const [title, setTitle] = useState(initial.title ?? '');
  const [folder, setFolder] = useState(initial.folder ?? '');
  const [tags, setTags] = useState((initial.tags ?? []).join(', '));
  const [notes, setNotes] = useState(initial.notes ?? '');
  const [url, setUrl] = useState(initial.url ?? '');
  const values = (): HandoutFormValues => ({
    title: title.trim(),
    folder: folder.trim(),
    tags: tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
    notes,
    url: url.trim(),
  });
  return { title, setTitle, folder, setFolder, tags, setTags, notes, setNotes, url, setUrl, values };
}

type Draft = ReturnType<typeof useHandoutDraft>;

/** The body: a link's address, or a note's markdown. */
function BodyField({ kind, d }: { kind: HandoutKind; d: Draft }) {
  if (kind === 'link') {
    return (
      <input
        className={common.input}
        placeholder="https://…"
        aria-label="Link"
        value={d.url}
        onChange={(e) => d.setUrl(e.target.value)}
        data-testid="handout-form-url"
      />
    );
  }
  return (
    kind === 'note' && (
      <textarea
        className={styles.notes}
        placeholder="Markdown: # heading, **bold**, *italic*, - list, [link](https://…)"
        aria-label="Note"
        value={d.notes}
        onChange={(e) => d.setNotes(e.target.value)}
        data-testid="handout-form-notes"
        rows={6}
      />
    )
  );
}

function FilingFields({ d }: { d: Draft }) {
  return (
    <div className={styles.formRow}>
      <input
        className={common.input}
        placeholder="Folder"
        aria-label="Folder"
        value={d.folder}
        onChange={(e) => d.setFolder(e.target.value)}
        data-testid="handout-form-folder"
      />
      <input
        className={common.input}
        placeholder="Tags, comma-separated"
        aria-label="Tags"
        value={d.tags}
        onChange={(e) => d.setTags(e.target.value)}
        data-testid="handout-form-tags"
      />
    </div>
  );
}

export default function HandoutForm({ kind, initial = {}, onSave, onCancel }: Props) {
  const d = useHandoutDraft(initial);
  const valid = isValidHandout(kind, d.notes, d.url);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    void onSave(d.values());
  };

  return (
    <form className={styles.form} onSubmit={submit} data-testid="handout-form" aria-label={`${kind} handout`}>
      <input
        className={common.input}
        placeholder="Title"
        aria-label="Title"
        value={d.title}
        onChange={(e) => d.setTitle(e.target.value)}
        data-testid="handout-form-title"
      />
      <BodyField kind={kind} d={d} />
      <FilingFields d={d} />
      <div className={styles.formRow}>
        <button type="submit" className={common.btnPrimary} disabled={!valid} data-testid="handout-form-save">
          Save
        </button>
        <button type="button" className={common.btnSecondary} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
